import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CreditCard, Banknote, Loader2, Minus, Plus, Receipt, ScrollText, Store } from 'lucide-react';
import api from '@/lib/api';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';

const eur = (v) => (v || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });

function Panier({ lignes, setQte, encaisser, envoi }) {
  const total = lignes.reduce((s, l) => s + l.quantite * l.prix_unitaire_ttc, 0);
  return (
    <Card className="lg:sticky lg:top-4"><CardContent className="p-4 space-y-3" data-testid="caveau-panier">
      <h2 className="font-semibold flex items-center gap-2"><Receipt className="w-4 h-4" />Ticket en cours</h2>
      {!lignes.length && <p className="text-sm text-stone-500">Touchez un vin pour l'ajouter.</p>}
      {lignes.map((l) => (
        <div key={l.article_id} className="flex items-center gap-2 text-sm">
          <span className="flex-1">{l.article_nom}</span>
          <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setQte(l.article_id, -1)} data-testid={`panier-moins-${l.article_id}`}><Minus className="w-3 h-3" /></Button>
          <span className="w-6 text-center">{l.quantite}</span>
          <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setQte(l.article_id, 1)} data-testid={`panier-plus-${l.article_id}`}><Plus className="w-3 h-3" /></Button>
          <span className="w-20 text-right">{eur(l.quantite * l.prix_unitaire_ttc)}</span>
        </div>
      ))}
      <div className="flex justify-between border-t pt-3 text-lg font-semibold"><span>Total TTC</span><span data-testid="panier-total">{eur(total)}</span></div>
      <div className="grid grid-cols-3 gap-2">
        {[['cb', 'Carte', CreditCard], ['especes', 'Espèces', Banknote], ['cheque', 'Chèque', ScrollText]].map(([m, l, I]) => (
          <Button key={m} disabled={!lignes.length || envoi} onClick={() => encaisser(m, total)} data-testid={`encaisser-${m}`}><I className="w-4 h-4 mr-1" />{l}</Button>
        ))}
      </div>
    </CardContent></Card>
  );
}

const MODES = { cb: 'Carte', especes: 'Espèces', cheque: 'Chèque', avoir: 'Avoir' };

export default function CaveauPage() {
  const { activeSociete } = useWorkspace();
  const [articles, setArticles] = useState([]);
  const [dash, setDash] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [panier, setPanier] = useState([]);
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(async () => {
    const [a, d, t] = await Promise.all([api.get('/caisse/articles'), api.get('/caisse/dashboard'), api.get('/caisse/tickets?limit=8')]);
    setArticles(a.data.articles || []);
    setDash(d.data);
    setTickets(t.data.tickets || []);
  }, []);
  useEffect(() => {
    if (!activeSociete?.id) return;
    setPanier([]);
    charger().catch(() => toast.error('Caisse indisponible'));
  }, [charger, activeSociete?.id]);

  const ajouter = (a) => setPanier((p) => p.find((l) => l.article_id === a.id)
    ? p.map((l) => (l.article_id === a.id ? { ...l, quantite: l.quantite + 1 } : l))
    : [...p, { article_id: a.id, article_nom: a.nom, quantite: 1, prix_unitaire_ttc: a.prix_ttc, taux_tva: a.taux_tva }]);
  const setQte = (id, delta) => setPanier((p) => p.map((l) => (l.article_id === id ? { ...l, quantite: l.quantite + delta } : l)).filter((l) => l.quantite > 0));

  const encaisser = async (mode, total) => {
    setEnvoi(true);
    try {
      if (!dash?.session_active) await api.post('/caisse/session/ouvrir', { fond_caisse: 150 });
      const r = await api.post('/caisse/tickets', { lignes: panier, mode_paiement: mode, montant_recu: mode === 'especes' ? Math.ceil(total) : null });
      toast.success(`Ticket ${r.data.ticket.numero} encaissé${r.data.ticket.rendu ? ` — rendu ${eur(r.data.ticket.rendu)}` : ''}`);
      setPanier([]);
      await charger();
    } catch (e) { toast.error(e.response?.data?.detail || 'Encaissement impossible'); }
    setEnvoi(false);
  };

  const s = dash?.session || {};
  const vins = useMemo(() => articles.filter((a) => a.actif !== false), [articles]);
  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="caveau-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold flex items-center gap-3"><Store className="w-7 h-7 text-purple-700" />Caveau & vente</h1>
          <p className="text-sm text-stone-500 mt-1">Caisse du caveau : stock bouteilles, tickets, TVA et clôture Z conformes.</p>
        </div>
        <div className="flex gap-4 text-sm">
          <div><p className="text-stone-500">Caisse du jour</p><p className="text-xl font-semibold" data-testid="caveau-ca-jour">{eur(s.total_ttc)}</p></div>
          <div><p className="text-stone-500">Tickets</p><p className="text-xl font-semibold">{s.nb_tickets || 0}</p></div>
        </div>
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 grid sm:grid-cols-2 xl:grid-cols-3 gap-3 content-start">
          {!vins.length && <p className="text-sm text-stone-500 sm:col-span-3 p-6 border rounded-xl bg-white" data-testid="caveau-vide">Pas de caveau dans cette société : choisissez la société qui gère la vente (ex. la cave) dans le sélecteur.</p>}
          {vins.map((a) => (
            <button key={a.id} onClick={() => ajouter(a)} data-testid={`caveau-article-${a.id}`}
              className="text-left p-4 rounded-xl border bg-white hover:border-purple-400 hover:shadow-md transition-shadow">
              <p className="font-medium text-sm leading-snug">{a.nom}</p>
              <div className="flex justify-between items-end mt-3">
                <span className="text-lg font-semibold text-purple-800">{eur(a.prix_ttc)}</span>
                <span className={`text-xs ${a.stock_actuel <= a.seuil_alerte ? 'text-red-600' : 'text-stone-500'}`}>{a.stock_actuel} en stock</span>
              </div>
            </button>
          ))}
        </div>
        <div className="space-y-4">
          <Panier lignes={panier} setQte={setQte} encaisser={encaisser} envoi={envoi} />
          <Card><CardContent className="p-4">
            <h3 className="font-semibold text-sm mb-2">Derniers tickets</h3>
            {tickets.map((t) => (
              <div key={t.id} className="flex justify-between text-sm py-1 border-b last:border-0" data-testid={`caveau-ticket-${t.id}`}>
                <span>{t.numero}</span><span className="text-stone-500">{MODES[t.mode_paiement] || t.mode_paiement}</span><span>{eur(t.total_ttc)}</span>
              </div>
            ))}
            {envoi && <Loader2 className="w-4 h-4 animate-spin mt-2" />}
          </CardContent></Card>
        </div>
      </div>
    </div>
  );
}
